import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  IconButton,
  Input,
  Badge,
  HStack,
  VStack,
  SimpleGrid,
  useColorModeValue,
  useDisclosure,
  Tooltip,
  Select,
  Textarea,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Collapse,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  FaSitemap,
  FaFolder,
  FaFolderOpen,
  FaLink,
  FaUnlink,
  FaMagic,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCog,
  FaBoxes,
  FaLayerGroup,
  FaChevronDown,
  FaChevronLeft,
  FaCode,
  FaArrowLeft,
  FaBroom,
} from 'react-icons/fa';

import Card from '../components/Card/Card';
import CardHeader from '../components/Card/CardHeader';
import CardBody from '../components/Card/CardBody';
import CustomModal from '../components/Modal';

import {
  getCodingPattern,
  updateCodingPattern,
  getProductCodings,
  createProductCoding,
  updateProductCoding,
  deleteProductCoding,
  connectVariant,
  bulkMatchVariants,
  purgeVariantsNotInCoding,
} from '../services/productCodingService';
import { getProducts } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';

/**
 * رنگ‌بندی سطوح مختلف درخت کدینگ
 */
const LEVEL_COLORS = [
  'blue',
  'purple',
  'teal',
  'green',
  'orange',
  'pink',
  'cyan',
];

/**
 * بررسی تطابق فیلتر جستجو با نود یا فرزندان آن
 */
function matchesSearch(node, term) {
  if (!term) return true;
  const lower = term.toLowerCase();
  const titleMatch = (node.title || '').toLowerCase().includes(lower);
  const codeMatch = (node.fullcode || '').toLowerCase().includes(lower);
  const shortMatch = (node.shortcode || '').toLowerCase().includes(lower);
  const skuMatch = (node.connectedVariant?.sku || '').toLowerCase().includes(lower);
  const childMatch = (node.children || []).some((child) => matchesSearch(child, term));
  return titleMatch || codeMatch || shortMatch || skuMatch || childMatch;
}

/**
 * کامپوننت آیتم درختی نود (TreeNodeItem)
 */
function TreeNodeItem({
  node,
  depth = 0,
  searchTerm,
  collapsedNodes,
  toggleCollapse,
  pattern,
  handleDisconnect,
  handleOpenConnect,
  handleOpenAddChild,
  handleOpenEdit,
  setNodeToDelete,
  onOpenDeleteModal,
}) {
  const bgNode = useColorModeValue('white', 'gray.750');
  const borderNode = useColorModeValue('gray.200', 'gray.700');
  const hoverNode = useColorModeValue('gray.50', 'whiteAlpha.50');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const greenBg = useColorModeValue('green.50', 'green.900');
  const greenBorder = useColorModeValue('green.200', 'green.700');
  const greenText800 = useColorModeValue('green.800', 'green.200');
  const greenText700 = useColorModeValue('green.700', 'green.300');
  const treeBranchBorder = useColorModeValue('gray.200', 'gray.600');

  const inline = Boolean(node._inline);
  const expanded = inline ? !collapsedNodes[node._id] : Boolean(collapsedNodes[node._id]);
  const shouldFetch = !inline && expanded && (node.childCount || 0) > 0;
  const { data: childrenResponse, isFetching: childrenLoading } = useQuery(
    ['productCodings', 'children', String(node._id)],
    () => getProductCodings({ parentId: node._id }),
    { enabled: shouldFetch, staleTime: 30000 }
  );

  if (!inline && !matchesSearch(node, searchTerm)) return null;

  const children = inline ? (node.children || []) : (childrenResponse?.data || []);
  const hasChildren = inline ? children.length > 0 : (node.childCount || 0) > 0;
  const isCollapsed = !expanded;
  const colorScheme = LEVEL_COLORS[(node.level - 1) % LEVEL_COLORS.length];
  const hasConnectedVariant = Boolean(node.connectedVariant?.productId);
  const isTerminal = (node.isLeaf || node.isTerminalLevel) && !hasChildren;

  return (
    <Box w="full" mb={2} position="relative">
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        p={3}
        bg={bgNode}
        border="1px solid"
        borderColor={node.connectedVariant?.productId ? 'green.300' : borderNode}
        borderRadius="xl"
        boxShadow="sm"
        _hover={{ borderColor: 'blue.400', bg: hoverNode }}
        transition="all 0.2s"
        gap={3}
      >
        {/* سمت راست: آیکون، بازکننده، عنوان و کدها */}
        <HStack spacing={3} align="center" flexWrap="wrap" flex={1}>
          {hasChildren ? (
            <IconButton
              size="xs"
              variant="ghost"
              icon={isCollapsed ? <FaChevronLeft /> : <FaChevronDown />}
              onClick={() => toggleCollapse(node._id)}
              aria-label="باز و بستن زیرشاخه"
            />
          ) : (
            <Box w={6} />
          )}

          <Tag size="md" colorScheme={colorScheme} variant="subtle" borderRadius="full">
            <TagLeftIcon as={hasChildren ? (isCollapsed ? FaFolder : FaFolderOpen) : FaBoxes} />
            <TagLabel fontWeight="bold">سطح {node.level}: {pattern?.levels?.[node.level - 1]?.title || 'سطح'}</TagLabel>
          </Tag>

          <HStack spacing={2} dir="ltr">
            <Badge
              colorScheme="blue"
              fontSize="xs"
              px={2.5}
              py={1}
              borderRadius="md"
              fontFamily="monospace"
              display="flex"
              alignItems="center"
              gap={1}
              title="کد کامل سلسله‌مراتبی (FullCode)"
            >
              <FaCode size={10} />
              {node.fullcode}
            </Badge>

            <Badge
              colorScheme="gray"
              fontSize="xs"
              px={2}
              py={0.5}
              borderRadius="md"
              fontFamily="monospace"
              title="کد کوتاه این سطح (ShortCode)"
            >
              [{node.shortcode}]
            </Badge>

            <Badge
              colorScheme="blackAlpha"
              fontSize="10px"
              px={1.5}
              py={0.5}
              borderRadius="sm"
              opacity={0.7}
              title="شاخص‌های مدل Nested Set"
            >
              L:{node.lft} | R:{node.rgt}
            </Badge>
          </HStack>

          <Text fontWeight="bold" fontSize="sm" color={headingColor}>
            {node.title}
          </Text>

          {node.description && (
            <Text fontSize="xs" color={textMuted} noOfLines={1} maxW="200px">
              ({node.description})
            </Text>
          )}
        </HStack>

        {/* سمت چپ: تنوع متصل شده و دکمه‌های عملیاتی */}
        <HStack spacing={2} justify={{ base: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
          {/* وضعیت تنوع متصل شده */}
          {isTerminal && (
            node.connectedVariant?.productId ? (
              <HStack
                bg={greenBg}
                border="1px solid"
                borderColor={greenBorder}
                px={2.5}
                py={1}
                borderRadius="lg"
                spacing={2}
              >
                <FaLink size={12} color="#38A169" />
                <VStack spacing={0} align="start">
                  <Text fontSize="xs" fontWeight="bold" color={greenText800}>
                    {node.connectedVariant.productName || 'محصول متصل'}
                  </Text>
                  <HStack spacing={1} fontSize="10px" color={greenText700}>
                    <Text>SKU: {node.connectedVariant.sku}</Text>
                    {node.variantLiveInfo && (
                      <>
                        <Text>•</Text>
                        <Text>موجودی: {node.variantLiveInfo.stock}</Text>
                        <Text>•</Text>
                        <Text>{node.variantLiveInfo.price.toLocaleString()} تومان</Text>
                      </>
                    )}
                  </HStack>
                </VStack>
                <Tooltip label="قطع ارتباط با این تنوع">
                  <IconButton
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    icon={<FaUnlink />}
                    onClick={() => handleDisconnect(node)}
                    aria-label="قطع اتصال"
                  />
                </Tooltip>
              </HStack>
            ) : (
              <Button
                size="xs"
                leftIcon={<FaLink />}
                colorScheme="purple"
                variant="outline"
                onClick={() => handleOpenConnect(node)}
              >
                اتصال به تنوع
              </Button>
            )
          )}

          {/* دکمه‌های عملیاتی نود */}
          {node.level < (pattern?.levels?.length || 4) && (
            hasConnectedVariant ? (
              <Tooltip label="این گره دارای تنوع متصل است و نمی‌تواند زیرمجموعه داشته باشد">
                <IconButton
                  size="xs"
                  colorScheme="gray"
                  icon={<FaPlus />}
                  isDisabled
                  opacity={0.5}
                  cursor="not-allowed"
                  aria-label="امکان افزودن زیرمجموعه وجود ندارد"
                />
              </Tooltip>
            ) : (
              <Tooltip label="افزودن زیرمجموعه به این شاخه">
                <IconButton
                  size="xs"
                  colorScheme="blue"
                  icon={<FaPlus />}
                  onClick={() => handleOpenAddChild(node)}
                  aria-label="افزودن زیرمجموعه"
                />
              </Tooltip>
            )
          )}

          <Tooltip label="ویرایش عنوان یا کد">
            <IconButton
              size="xs"
              colorScheme="gray"
              icon={<FaEdit />}
              onClick={() => handleOpenEdit(node)}
              aria-label="ویرایش نود"
            />
          </Tooltip>

          <Tooltip label="حذف نود و کلیه زیرشاخه‌ها">
            <IconButton
              size="xs"
              colorScheme="red"
              variant="ghost"
              icon={<FaTrash />}
              onClick={() => {
                setNodeToDelete(node);
                onOpenDeleteModal();
              }}
              aria-label="حذف نود"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* فرزندان نود با فرورفتگی درخت */}
      {hasChildren && (
        <Collapse in={!isCollapsed} animateOpacity>
          <Box pr={{ base: 4, md: 8 }} mt={2} borderRight="2px dashed" borderColor={treeBranchBorder}>
            {childrenLoading && children.length === 0 ? (
              <Flex py={3} justify="flex-start">
                <Spinner size="sm" color="blue.400" />
              </Flex>
            ) : (
              children.map((child) => (
                <TreeNodeItem
                  key={child._id}
                  node={inline ? child : { ...child, _inline: false }}
                  depth={depth + 1}
                  searchTerm={inline ? '' : searchTerm}
                  collapsedNodes={collapsedNodes}
                  toggleCollapse={toggleCollapse}
                  pattern={pattern}
                  handleDisconnect={handleDisconnect}
                  handleOpenConnect={handleOpenConnect}
                  handleOpenAddChild={handleOpenAddChild}
                  handleOpenEdit={handleOpenEdit}
                  setNodeToDelete={setNodeToDelete}
                  onOpenDeleteModal={onOpenDeleteModal}
                />
              ))
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default function ProductCoding() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const strictCodingMatch = user?.settings?.strictCodingMatch === true;

  // پالت‌های رنگی تم تاریک/روشن در سطح کامپوننت اصلی
  const bgCard = useColorModeValue('white', 'gray.800');
  const borderCard = useColorModeValue('gray.200', 'gray.700');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const ribbonBg = useColorModeValue('gray.50', 'gray.900');
  const ribbonBorder = useColorModeValue('gray.200', 'gray.700');

  // رنگ‌های اختصاصی مودال‌ها
  const modalBlueBg = useColorModeValue('blue.50', 'blue.900');
  const modalGreenBg = useColorModeValue('green.50', 'green.900');
  const modalGrayBg = useColorModeValue('gray.50', 'gray.700');
  const modalOrangeBg = useColorModeValue('orange.50', 'orange.900');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const previewBoxBg = useColorModeValue('blue.50', 'blue.900');
  const previewTextColor = useColorModeValue('blue.700', 'blue.200');

  // استیت‌های فیلتر و باز/بسته بودن شاخه‌ها
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [collapsedNodes, setCollapsedNodes] = useState({});

  // استیت مودال نود (ایجاد/ویرایش)
  const {
    isOpen: isNodeModalOpen,
    onOpen: onOpenNodeModal,
    onClose: onCloseNodeModal,
  } = useDisclosure();
  const [nodeModalMode, setNodeModalMode] = useState('create');
  const [activeParentNode, setActiveParentNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [nodeTitle, setNodeTitle] = useState('');
  const [nodeShortCode, setNodeShortCode] = useState('');
  const [nodeDescription, setNodeDescription] = useState('');

  // استیت مودال تنظیمات الگو
  const {
    isOpen: isPatternModalOpen,
    onOpen: onOpenPatternModal,
    onClose: onClosePatternModal,
  } = useDisclosure();
  const [patternTitle, setPatternTitle] = useState('');
  const [patternSeparator, setPatternSeparator] = useState('');
  const [patternLevels, setPatternLevels] = useState([]);

  // استیت مودال اتصال دستی به تنوع محصول
  const {
    isOpen: isConnectModalOpen,
    onOpen: onOpenConnectModal,
    onClose: onCloseConnectModal,
  } = useDisclosure();
  const [connectingNode, setConnectingNode] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedLogicalVariantId, setSelectedLogicalVariantId] = useState('');

  // استیت مودال گزارش اتصال جمعی
  const {
    isOpen: isBulkModalOpen,
    onOpen: onOpenBulkModal,
    onClose: onCloseBulkModal,
  } = useDisclosure();
  const [bulkReportData, setBulkReportData] = useState(null);

  // استیت مودال تایید حذف
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure();
  const [nodeToDelete, setNodeToDelete] = useState(null);

  const {
    isOpen: isPurgeModalOpen,
    onOpen: onOpenPurgeModal,
    onClose: onClosePurgeModal,
  } = useDisclosure();

  // ==========================================
  // دریافت اطلاعات از سرور (React Query)
  // ==========================================
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isSearching = debouncedSearch.length >= 2;

  const {
    data: codingsResponse,
    isLoading: isLoadingCodings,
    refetch: refetchCodings,
  } = useQuery(['productCodings', 'root'], () => getProductCodings({ parentId: 'root' }));

  const {
    data: searchResponse,
    isLoading: isLoadingSearch,
  } = useQuery(
    ['productCodings', 'search', debouncedSearch],
    () => getProductCodings({ q: debouncedSearch }),
    { enabled: isSearching }
  );

  const {
    data: patternResponse,
    isLoading: isLoadingPattern,
  } = useQuery(['codingPattern'], getCodingPattern);

  const { data: productsResponse } = useQuery(
    ['productsForCoding'],
    () => getProducts({ limit: 1000 }),
    { enabled: isConnectModalOpen }
  );

  const pattern = patternResponse?.data || codingsResponse?.pattern;
  const rootNodes = useMemo(() => codingsResponse?.data || [], [codingsResponse]);
  const products = useMemo(() => productsResponse?.data?.products || productsResponse?.data || [], [productsResponse]);

  const searchTree = useMemo(() => {
    const rawNodes = searchResponse?.data || [];
    if (!rawNodes.length) return [];
    const nodeMap = new Map();
    rawNodes.forEach((node) => {
      nodeMap.set(String(node._id), { ...node, _inline: true, children: [] });
    });
    const roots = [];
    rawNodes.forEach((node) => {
      const current = nodeMap.get(String(node._id));
      if (node.parentId && nodeMap.has(String(node.parentId))) {
        nodeMap.get(String(node.parentId)).children.push(current);
      } else {
        roots.push(current);
      }
    });
    return roots;
  }, [searchResponse]);

  const treeData = isSearching ? searchTree : rootNodes;

  const stats = useMemo(() => {
    const meta = codingsResponse?.meta;
    if (meta) {
      return {
        total: meta.total,
        connected: meta.connected,
        terminalCount: meta.leaves,
        unconnectedTerminal: meta.unconnectedLeaves,
        maxDepth: pattern?.levels?.length || 0,
      };
    }
    return {
      total: rootNodes.length,
      connected: 0,
      terminalCount: 0,
      unconnectedTerminal: 0,
      maxDepth: pattern?.levels?.length || 0,
    };
  }, [codingsResponse, rootNodes.length, pattern]);

  // ==========================================
  // جهش‌ها (Mutations)
  // ==========================================
  const createMutation = useMutation(createProductCoding, {
    onSuccess: (res) => {
      toast.success(res.message || 'کدینگ با موفقیت ثبت شد');
      queryClient.invalidateQueries(['productCodings']);
      onCloseNodeModal();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'خطا در ثبت کدینگ');
    },
  });

  const updateMutation = useMutation(
    ({ id, data }) => updateProductCoding(id, data),
    {
      onSuccess: (res) => {
        toast.success(res.message || 'کدینگ با موفقیت ویرایش شد');
        queryClient.invalidateQueries(['productCodings']);
        onCloseNodeModal();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'خطا در ویرایش کدینگ');
      },
    }
  );

  const deleteMutation = useMutation(deleteProductCoding, {
    onSuccess: (res) => {
      toast.success(res.message || 'کدینگ با موفقیت حذف شد');
      queryClient.invalidateQueries(['productCodings']);
      onCloseDeleteModal();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'خطا در حذف کدینگ');
    },
  });

  const patternMutation = useMutation(updateCodingPattern, {
    onSuccess: (res) => {
      toast.success(res.message || 'الگوی کدینگ با موفقیت به‌روزرسانی شد');
      queryClient.invalidateQueries(['codingPattern']);
      queryClient.invalidateQueries(['productCodings']);
      onClosePatternModal();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'خطا در به‌روزرسانی الگو');
    },
  });

  const connectMutation = useMutation(
    ({ id, data }) => connectVariant(id, data),
    {
      onSuccess: (res) => {
        toast.success(res.message || 'تغییرات تنوع اعمال شد');
        queryClient.invalidateQueries(['productCodings']);
        onCloseConnectModal();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'خطا در اتصال تنوع');
      },
    }
  );

  const bulkMutation = useMutation(bulkMatchVariants, {
    onSuccess: (res) => {
      setBulkReportData(res);
      onOpenBulkModal();
      queryClient.invalidateQueries(['productCodings']);
      toast.success(res.message || 'اتصال جمعی با موفقیت انجام شد');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'خطا در اجرای اتصال جمعی');
    },
  });

  const purgeMutation = useMutation(purgeVariantsNotInCoding, {
    onSuccess: (res) => {
      toast.success(res.message || 'پاکسازی انجام شد');
      queryClient.invalidateQueries(['productCodings']);
      queryClient.invalidateQueries(['products']);
      onClosePurgeModal();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'خطا در پاکسازی تنوع‌های خارج از کدینگ');
    },
  });

  // ==========================================
  // هندلرهای مودال‌ها و عملیات‌ها
  // ==========================================
  const toggleCollapse = (nodeId) => {
    setCollapsedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const handleExpandAll = () => {
    toast('هر سطح با باز کردن همان شاخه بارگذاری می‌شود');
  };
  const handleCollapseAll = () => setCollapsedNodes({});

  const handleOpenAddRoot = () => {
    setNodeModalMode('create');
    setActiveParentNode(null);
    setEditingNode(null);
    setNodeTitle('');
    setNodeShortCode('');
    setNodeDescription('');
    onOpenNodeModal();
  };

  const handleOpenAddChild = (parent) => {
    const nextLevel = parent.level + 1;
    const maxLevel = pattern?.levels?.length || 4;
    if (nextLevel > maxLevel) {
      toast.error(`امکان افزودن زیرمجموعه فراتر از سطح ${maxLevel} وجود ندارد`);
      return;
    }
    setNodeModalMode('create');
    setActiveParentNode(parent);
    setEditingNode(null);
    setNodeTitle('');
    setNodeShortCode('');
    setNodeDescription('');
    onOpenNodeModal();
  };

  const handleOpenEdit = (node) => {
    setNodeModalMode('edit');
    setEditingNode(node);
    setActiveParentNode(null);
    setNodeTitle(node.title);
    setNodeShortCode(node.shortcode);
    setNodeDescription(node.description || '');
    onOpenNodeModal();
  };

  const handleSaveNode = () => {
    if (!nodeTitle.trim()) {
      toast.error('لطفاً عنوان کدینگ را وارد کنید');
      return;
    }
    if (!nodeShortCode.trim()) {
      toast.error('لطفاً کد کوتاه را وارد کنید');
      return;
    }

    if (nodeModalMode === 'create') {
      createMutation.mutate({
        parentId: activeParentNode?._id || null,
        title: nodeTitle.trim(),
        shortcode: nodeShortCode.trim(),
        description: nodeDescription.trim(),
      });
    } else {
      updateMutation.mutate({
        id: editingNode._id,
        data: {
          title: nodeTitle.trim(),
          shortcode: nodeShortCode.trim(),
          description: nodeDescription.trim(),
        },
      });
    }
  };

  const handleOpenPatternSettings = () => {
    if (!pattern) return;
    setPatternTitle(pattern.title || 'الگوی کدینگ کالا');
    setPatternSeparator(pattern.separator ?? '');
    setPatternLevels(
      (pattern.levels || []).map((l) => ({
        level: l.level,
        title: l.title,
        length: l.length,
        description: l.description || '',
      }))
    );
    onOpenPatternModal();
  };

  const handleAddPatternLevel = () => {
    setPatternLevels((prev) => [
      ...prev,
      {
        level: prev.length + 1,
        title: `سطح ${prev.length + 1}`,
        length: 2,
        description: '',
      },
    ]);
  };

  const handleRemovePatternLevel = (index) => {
    if (patternLevels.length <= 1) {
      toast.error('حداقل یک سطح کدینگ باید در الگو وجود داشته باشد');
      return;
    }
    setPatternLevels((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((lvl, idx) => ({ ...lvl, level: idx + 1 }));
    });
  };

  const handleSavePattern = () => {
    if (!patternTitle.trim()) {
      toast.error('عنوان الگو الزامی است');
      return;
    }
    patternMutation.mutate({
      title: patternTitle.trim(),
      separator: patternSeparator,
      levels: patternLevels,
    });
  };

  const handleOpenConnect = (node) => {
    setConnectingNode(node);
    setSelectedProductId(node.connectedVariant?.productId || '');
    setSelectedLogicalVariantId(node.connectedVariant?.logicalVariantId || '');
    onOpenConnectModal();
  };

  const handleSaveConnect = () => {
    if (!selectedProductId || !selectedLogicalVariantId) {
      toast.error('لطفاً محصول و تنوع منطقی را انتخاب کنید');
      return;
    }
    connectMutation.mutate({
      id: connectingNode._id,
      data: {
        productId: selectedProductId,
        logicalVariantId: selectedLogicalVariantId,
      },
    });
  };

  const handleDisconnect = (node) => {
    connectMutation.mutate({
      id: node._id,
      data: { disconnect: true },
    });
  };

  // پیش‌نمایش FullCode هنگام تایپ در مودال
  const calculatedFullCodePreview = useMemo(() => {
    if (!nodeShortCode) return '---';
    const sep = pattern?.separator || '';
    const targetLength = activeParentNode
      ? pattern?.levels?.find((l) => l.level === activeParentNode.level + 1)?.length || 2
      : pattern?.levels?.[0]?.length || 2;

    let padded = nodeShortCode.trim();
    if (/^\d+$/.test(padded) && padded.length < targetLength) {
      padded = padded.padStart(targetLength, '0');
    }

    if (nodeModalMode === 'edit') {
      if (editingNode?.parentId && editingNode.fullcode) {
        const oldShort = editingNode.shortcode || '';
        let parentFull = editingNode.fullcode;
        if (oldShort && parentFull.endsWith(oldShort)) {
          parentFull = parentFull.slice(0, -oldShort.length);
          if (sep && parentFull.endsWith(sep)) {
            parentFull = parentFull.slice(0, -sep.length);
          }
        }
        return parentFull ? `${parentFull}${sep}${padded}` : padded;
      }
      return padded;
    }

    return activeParentNode ? `${activeParentNode.fullcode}${sep}${padded}` : padded;
  }, [nodeShortCode, activeParentNode, editingNode, pattern, nodeModalMode]);

  // تنوع‌های منطقی محصول انتخاب شده در مودال اتصال
  const selectedProductLogicalVariants = useMemo(() => {
    if (!selectedProductId) return [];
    const prod = products.find((p) => String(p._id) === String(selectedProductId));
    return prod?.logicalVariants || [];
  }, [selectedProductId, products]);

  return (
    <Box pt={{ base: '120px', md: '75px' }} px={{ base: '15px', md: '30px' }} pb="30px">
      {/* هدر صفحه و دکمه‌های اکشن اصلی */}
      <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" align={{ base: 'stretch', lg: 'center' }} mb={6} gap={4}>
        <Box>
          <HStack spacing={3}>
            <FaSitemap size={28} color="#3182CE" />
            <Heading size="lg" fontWeight="extrabold">
              کدینگ کالا
            </Heading>
          </HStack>
          <Text fontSize="sm" color={textMuted} mt={1}>
            ساختار درختی طبقه‌بندی کالا با مدل Nested Set و امکان اتصال به تنوع‌های منطقی
          </Text>
        </Box>

        <HStack spacing={3} flexWrap="wrap">
          <Button
            leftIcon={<FaCog />}
            colorScheme="gray"
            variant="solid"
            onClick={handleOpenPatternSettings}
            size="md"
          >
            تنظیمات الگوی کدینگ
          </Button>

          <Button
            leftIcon={<FaMagic />}
            colorScheme="teal"
            variant="solid"
            isLoading={bulkMutation.isLoading}
            loadingText="در حال بررسی..."
            onClick={() => bulkMutation.mutate()}
            size="md"
          >
            اتصال جمعی تنوع‌ها بر اساس SKU
          </Button>

          {strictCodingMatch && (
            <Button
              leftIcon={<FaBroom />}
              colorScheme="orange"
              variant="solid"
              onClick={onOpenPurgeModal}
              size="md"
            >
              پاکسازی تنوع‌های خارج از کدینگ
            </Button>
          )}

          <Button
            leftIcon={<FaPlus />}
            colorScheme="blue"
            onClick={handleOpenAddRoot}
            size="md"
          >
            افزودن سرگروه اصلی
          </Button>
        </HStack>
      </Flex>

      {/* نوار تجسم الگوی کدینگ (Coding Pattern Ribbon - مشابه salam-sales) */}
      <Card mb={6} bg={bgCard} p={4} borderRadius="xl" boxShadow="sm">
        <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'start', md: 'center' }} mb={3}>
          <HStack spacing={2}>
            <FaLayerGroup color="#4A5568" />
            <Text fontWeight="bold" fontSize="sm">
              الگوی کدینگ فعال: {pattern?.title || 'الگوی کدینگ کالا'}
            </Text>
            <Badge colorScheme={pattern?.separator ? 'blue' : 'gray'}>
              جداکننده: {pattern?.separator ? `« ${pattern.separator} »` : 'بدون جداکننده'}
            </Badge>
          </HStack>

          <Text fontSize="xs" color={textMuted}>
            به ازای هر کاربر فقط یک الگوی کدینگ تعریف می‌شود که سطوح و طول کدهای هر سطح را تعیین می‌کند.
          </Text>
        </Flex>

        <Flex align="center" gap={2} overflowX="auto" pb={2} pt={1}>
          {(pattern?.levels || []).map((lvl, index) => {
            const isLast = index === (pattern?.levels || []).length - 1;
            const colorScheme = LEVEL_COLORS[index % LEVEL_COLORS.length];
            return (
              <React.Fragment key={lvl.level}>
                <Box
                  p={3}
                  bg={ribbonBg}
                  border="1px solid"
                  borderColor={ribbonBorder}
                  borderRadius="xl"
                  minW="160px"
                  textAlign="center"
                  position="relative"
                >
                  <Tag size="sm" colorScheme={colorScheme} mb={1}>
                    سطح {lvl.level}
                  </Tag>
                  <Text fontWeight="bold" fontSize="sm">
                    {lvl.title}
                  </Text>
                  <Text fontSize="xs" color={textMuted} mt={0.5}>
                    طول کد: {lvl.length} رقم
                  </Text>
                  {isLast && (
                    <Badge colorScheme="green" fontSize="10px" mt={1}>
                      قابلیت اتصال به تنوع
                    </Badge>
                  )}
                </Box>

                {!isLast && (
                  <Box color={textMuted} px={1}>
                    <FaArrowLeft size={16} />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Flex>
      </Card>

      {/* کارت‌های خلاصه وضعیت و آمار */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Card bg={bgCard} p={4} borderRadius="xl" boxShadow="sm">
          <Text fontSize="xs" color={textMuted}>کل کدهای تعریف‌شده</Text>
          <Heading size="md" color="blue.500" mt={1}>{stats.total} گره</Heading>
        </Card>
        <Card bg={bgCard} p={4} borderRadius="xl" boxShadow="sm">
          <Text fontSize="xs" color={textMuted}>متصل به تنوع منطقی</Text>
          <Heading size="md" color="green.500" mt={1}>{stats.connected} تنوع</Heading>
        </Card>
        <Card bg={bgCard} p={4} borderRadius="xl" boxShadow="sm">
          <Text fontSize="xs" color={textMuted}>کدهای انتهایی بدون اتصال</Text>
          <Heading size="md" color="orange.500" mt={1}>{stats.unconnectedTerminal} مورد</Heading>
        </Card>
        <Card bg={bgCard} p={4} borderRadius="xl" boxShadow="sm">
          <Text fontSize="xs" color={textMuted}>تعداد سطوح الگو</Text>
          <Heading size="md" color="purple.500" mt={1}>{stats.maxDepth} سطح</Heading>
        </Card>
      </SimpleGrid>

      {/* بخش اصلی درخت کدینگ کالا */}
      <Card bg={bgCard} p={5} borderRadius="xl" boxShadow="md">
        <CardHeader mb={4} p={0}>
          <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3}>
            <HStack spacing={2} flex={1} maxW={{ md: '360px' }}>
              <Input
                placeholder="جستجو در عنوان، کد کامل، کد کوتاه یا SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                borderRadius="lg"
              />
            </HStack>

            <HStack spacing={2}>
              <Button size="xs" variant="outline" onClick={handleExpandAll}>
                باز کردن همه
              </Button>
              <Button size="xs" variant="outline" onClick={handleCollapseAll}>
                بستن همه
              </Button>
              <Button size="xs" colorScheme="blue" variant="ghost" onClick={() => refetchCodings()}>
                تازه‌سازی
              </Button>
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody p={0}>
          {(isLoadingCodings || isLoadingPattern || (isSearching && isLoadingSearch)) ? (
            <Flex justify="center" align="center" py={12}>
              <Spinner size="lg" color="blue.500" />
            </Flex>
          ) : treeData.length === 0 ? (
            <Box textAlign="center" py={12}>
              <FaSitemap size={48} color="#CBD5E0" style={{ margin: '0 auto 12px' }} />
              <Heading size="md" color={textMuted} mb={2}>
                {isSearching ? 'نتیجه‌ای برای این جستجو پیدا نشد' : 'هنوز هیچ کدی در درخت کدینگ کالا ثبت نشده است'}
              </Heading>
              <Text fontSize="sm" color={textMuted} mb={4}>
                {isSearching
                  ? 'عبارت دیگری را امتحان کنید. جستجو روی سرور انجام می‌شود و فقط همان شاخه‌ها بارگذاری می‌شوند.'
                  : 'برای شروع، اولین سرگروه اصلی را با فشردن دکمه زیر ایجاد نمایید.'}
              </Text>
              {!isSearching && (
                <Button leftIcon={<FaPlus />} colorScheme="blue" onClick={handleOpenAddRoot}>
                  افزودن اولین سرگروه
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              {treeData.map((node) => (
                <TreeNodeItem
                  key={node._id}
                  node={node}
                  depth={0}
                  searchTerm=""
                  collapsedNodes={collapsedNodes}
                  toggleCollapse={toggleCollapse}
                  pattern={pattern}
                  handleDisconnect={handleDisconnect}
                  handleOpenConnect={handleOpenConnect}
                  handleOpenAddChild={handleOpenAddChild}
                  handleOpenEdit={handleOpenEdit}
                  setNodeToDelete={setNodeToDelete}
                  onOpenDeleteModal={onOpenDeleteModal}
                />
              ))}
            </Box>
          )}
        </CardBody>
      </Card>

      {/* ========================================================= */}
      {/* ۱. مودال افزودن / ویرایش نود کدینگ */}
      {/* ========================================================= */}
      <CustomModal
        isOpen={isNodeModalOpen}
        onClose={onCloseNodeModal}
        title={nodeModalMode === 'create' ? (activeParentNode ? `افزودن زیرمجموعه به «${activeParentNode.title}»` : 'افزودن سرگروه اصلی') : 'ویرایش کدینگ'}
        size="lg"
      >
        <VStack spacing={4} align="stretch">
          {activeParentNode && (
            <Alert status="info" borderRadius="md" fontSize="xs">
              <AlertIcon />
              والد این نود: <strong>{activeParentNode.title}</strong> (کد کامل والد: {activeParentNode.fullcode})
            </Alert>
          )}

          <Box>
            <Text fontSize="xs" fontWeight="bold" mb={1}>
              کد کوتاه این سطح:
            </Text>
            <HStack>
              <Input
                placeholder={`مثلاً 01 یا 001`}
                value={nodeShortCode}
                onChange={(e) => setNodeShortCode(e.target.value)}
                maxLength={10}
              />
              <Tag size="lg" colorScheme="blue" whiteSpace="nowrap">
                طول تعریف شده: {
                  activeParentNode
                    ? pattern?.levels?.find((l) => l.level === activeParentNode.level + 1)?.length || 2
                    : pattern?.levels?.[0]?.length || 2
                } رقم
              </Tag>
            </HStack>
            <Text fontSize="xs" color={textMuted} mt={1}>
              اگر عدد وارد کنید و تعداد ارقام کمتر از طول استاندارد باشد، به طور خودکار صفر به ابتدا افزوده می‌شود.
            </Text>
          </Box>

          <Box bg={previewBoxBg} p={3} borderRadius="md">
            <Text fontSize="xs" fontWeight="bold" color="blue.500" mb={1}>
              پیش‌نمایش کد کامل تولیدی (FullCode):
            </Text>
            <Text fontSize="md" fontWeight="extrabold" fontFamily="monospace" color={previewTextColor} dir="ltr">
              {calculatedFullCodePreview}
            </Text>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="bold" mb={1}>
              عنوان کدینگ: *
            </Text>
            <Input
              placeholder="مثلاً پوشاک، پیراهن آستین کوتاه، سایز L..."
              value={nodeTitle}
              onChange={(e) => setNodeTitle(e.target.value)}
            />
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="bold" mb={1}>
              توضیحات تکمیلی (اختیاری):
            </Text>
            <Textarea
              placeholder="توضیحات و یادداشت‌های مرتبط با این کدینگ..."
              value={nodeDescription}
              onChange={(e) => setNodeDescription(e.target.value)}
              rows={2}
            />
          </Box>

          <Flex justify="flex-end" gap={2} pt={3}>
            <Button variant="ghost" onClick={onCloseNodeModal}>
              انصراف
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveNode}
              isLoading={createMutation.isLoading || updateMutation.isLoading}
            >
              {nodeModalMode === 'create' ? 'ثبت و افزودن به درخت' : 'ذخیره تغییرات'}
            </Button>
          </Flex>
        </VStack>
      </CustomModal>

      {/* ========================================================= */}
      {/* ۲. مودال تنظیمات الگوی کدینگ */}
      {/* ========================================================= */}
      <CustomModal
        isOpen={isPatternModalOpen}
        onClose={onClosePatternModal}
        title="تنظیمات الگوی کدینگ کالا"
        size="xl"
      >
        <VStack spacing={4} align="stretch">
          <Text fontSize="xs" color={textMuted}>
            این الگو ساختار سلسله‌مراتبی کالاها را در سیستم شما معین می‌کند. هر کاربر تنها یک الگوی اختصاصی دارد.
          </Text>

          <Box>
            <Text fontSize="xs" fontWeight="bold" mb={1}>عنوان الگو:</Text>
            <Input
              value={patternTitle}
              onChange={(e) => setPatternTitle(e.target.value)}
              placeholder="الگوی کدینگ کالا"
            />
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="bold" mb={1}>کاراکتر جداکننده کدها (Separator):</Text>
            <Select
              value={patternSeparator}
              onChange={(e) => setPatternSeparator(e.target.value)}
            >
              <option value="">بدون جداکننده (پیوسته، مثلاً ۰۱۰۲۰۳)</option>
              <option value=".">نقطه . (مثلاً ۰۱.۰۲.۰۳)</option>
              <option value="-">خط فاصله - (مثلاً ۰۱-۰۲-۰۳)</option>
              <option value="/">اسلش / (مثلاً ۰۱/۰۲/۰۳)</option>
            </Select>
          </Box>

          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="xs" fontWeight="bold">سطوح کدینگ (از بالا به پایین):</Text>
              <Button size="xs" leftIcon={<FaPlus />} colorScheme="blue" onClick={handleAddPatternLevel}>
                افزودن سطح جدید
              </Button>
            </Flex>

            <VStack spacing={2} align="stretch">
              {patternLevels.map((lvl, idx) => (
                <HStack key={idx} p={2} bg={ribbonBg} borderRadius="md" spacing={3}>
                  <Tag size="sm" colorScheme="blue">{lvl.level}</Tag>
                  <Input
                    size="sm"
                    value={lvl.title}
                    onChange={(e) => {
                      const next = [...patternLevels];
                      next[idx].title = e.target.value;
                      setPatternLevels(next);
                    }}
                    placeholder="عنوان سطح (مثلاً گروه اصلی)"
                    flex={2}
                  />
                  <HStack spacing={1} flex={1}>
                    <Text fontSize="xs">طول:</Text>
                    <Select
                      size="sm"
                      value={lvl.length}
                      onChange={(e) => {
                        const next = [...patternLevels];
                        next[idx].length = Number(e.target.value);
                        setPatternLevels(next);
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <option key={n} value={n}>{n} رقم</option>
                      ))}
                    </Select>
                  </HStack>
                  <IconButton
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    icon={<FaTrash />}
                    onClick={() => handleRemovePatternLevel(idx)}
                    aria-label="حذف سطح"
                  />
                </HStack>
              ))}
            </VStack>
          </Box>

          <Flex justify="flex-end" gap={2} pt={3}>
            <Button variant="ghost" onClick={onClosePatternModal}>انصراف</Button>
            <Button
              colorScheme="blue"
              onClick={handleSavePattern}
              isLoading={patternMutation.isLoading}
            >
              ذخیره تنظیمات الگو
            </Button>
          </Flex>
        </VStack>
      </CustomModal>

      {/* ========================================================= */}
      {/* ۳. مودال اتصال دستی نود به تنوع محصول */}
      {/* ========================================================= */}
      <CustomModal
        isOpen={isConnectModalOpen}
        onClose={onCloseConnectModal}
        title={`اتصال کدینگ «${connectingNode?.title || ''}» به تنوع محصول`}
        size="lg"
      >
        <VStack spacing={4} align="stretch">
          <Alert status="info" borderRadius="md" fontSize="xs">
            <AlertIcon />
            کد کامل این شاخه: <strong>{connectingNode?.fullcode}</strong>
          </Alert>

          <Box>
            <Text fontSize="xs" fontWeight="bold" mb={1}>انتخاب محصول:</Text>
            <Select
              placeholder="-- یک محصول را انتخاب کنید --"
              value={selectedProductId}
              onChange={(e) => {
                setSelectedProductId(e.target.value);
                setSelectedLogicalVariantId('');
              }}
            >
              {products.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name} {p.accountingCode ? `(کد: ${p.accountingCode})` : ''}
                </option>
              ))}
            </Select>
          </Box>

          {selectedProductId && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1}>انتخاب تنوع منطقی:</Text>
              {selectedProductLogicalVariants.length === 0 ? (
                <Alert status="warning" fontSize="xs" borderRadius="md">
                  این محصول تنوع منطقی تعریف‌شده‌ای ندارد.
                </Alert>
              ) : (
                <Select
                  placeholder="-- یک تنوع منطقی را انتخاب کنید --"
                  value={selectedLogicalVariantId}
                  onChange={(e) => setSelectedLogicalVariantId(e.target.value)}
                >
                  {selectedProductLogicalVariants.map((v) => (
                    <option key={v._id} value={v._id}>
                      کد حسابداری / SKU: {v.accountingCode || 'نامشخص'} | موجودی: {v.stock} | قیمت: {v.price?.toLocaleString()} تومان
                    </option>
                  ))}
                </Select>
              )}
            </Box>
          )}

          <Flex justify="flex-end" gap={2} pt={3}>
            <Button variant="ghost" onClick={onCloseConnectModal}>انصراف</Button>
            <Button
              colorScheme="purple"
              onClick={handleSaveConnect}
              isLoading={connectMutation.isLoading}
              isDisabled={!selectedLogicalVariantId}
            >
              ثبت اتصال
            </Button>
          </Flex>
        </VStack>
      </CustomModal>

      {/* ========================================================= */}
      {/* ۴. مودال گزارش اتصال جمعی بر اساس SKU */}
      {/* ========================================================= */}
      <CustomModal
        isOpen={isBulkModalOpen}
        onClose={onCloseBulkModal}
        title="گزارش اتصال جمعی تنوع‌ها بر اساس SKU"
        size="2xl"
      >
        <VStack spacing={4} align="stretch">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <Box p={3} bg={modalBlueBg} borderRadius="lg" textAlign="center">
              <Text fontSize="xs">کل بررسی‌شده</Text>
              <Heading size="sm" mt={1}>{bulkReportData?.stats?.totalChecked || 0}</Heading>
            </Box>
            <Box p={3} bg={modalGreenBg} borderRadius="lg" textAlign="center">
              <Text fontSize="xs">اتصال جدید</Text>
              <Heading size="sm" color="green.500" mt={1}>{bulkReportData?.stats?.newlyConnected || 0}</Heading>
            </Box>
            <Box p={3} bg={modalGrayBg} borderRadius="lg" textAlign="center">
              <Text fontSize="xs">از قبل متصل</Text>
              <Heading size="sm" mt={1}>{bulkReportData?.stats?.alreadyConnected || 0}</Heading>
            </Box>
            <Box p={3} bg={modalOrangeBg} borderRadius="lg" textAlign="center">
              <Text fontSize="xs">نامنطبق</Text>
              <Heading size="sm" color="orange.500" mt={1}>{bulkReportData?.stats?.unmatched || 0}</Heading>
            </Box>
          </SimpleGrid>

          {bulkReportData?.stats?.matchedDetails?.length > 0 ? (
            <Box maxH="320px" overflowY="auto" border="1px solid" borderColor={borderCard} borderRadius="lg">
              <Table size="sm" variant="simple">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>کد کامل (FullCode)</Th>
                    <Th>عنوان کدینگ</Th>
                    <Th>محصول متصل شده</Th>
                    <Th>SKU تنوع</Th>
                    <Th>وضعیت</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {bulkReportData.stats.matchedDetails.map((item, idx) => (
                    <Tr key={idx}>
                      <Td fontFamily="monospace" dir="ltr">{item.fullcode}</Td>
                      <Td>{item.codingTitle}</Td>
                      <Td>{item.productName}</Td>
                      <Td fontFamily="monospace" dir="ltr">{item.sku}</Td>
                      <Td>
                        <Badge colorScheme={item.status.includes('جدید') ? 'green' : 'gray'}>
                          {item.status}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Alert status="warning" fontSize="xs" borderRadius="md">
              <AlertIcon />
              موردی منطبق با SKUهای تنوع‌های منطقی یافت نشد. اطمینان حاصل کنید که SKUهای تنوع‌های منطقی با کد کامل (FullCode) کدینگ‌ها یکسان باشند.
            </Alert>
          )}

          <Flex justify="flex-end" pt={2}>
            <Button colorScheme="blue" onClick={onCloseBulkModal}>
              بستن
            </Button>
          </Flex>
        </VStack>
      </CustomModal>

      {/* ========================================================= */}
      {/* ۵. مودال تایید حذف نود */}
      {/* ========================================================= */}
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={onCloseDeleteModal}
        title="تایید حذف کدینگ"
      >
        <VStack spacing={4} align="stretch">
          <Alert status="error" borderRadius="md" fontSize="xs">
            <AlertIcon />
            آیا از حذف کدینگ «<strong>{nodeToDelete?.title}</strong>» با کد کامل «<strong>{nodeToDelete?.fullcode}</strong>» اطمینان دارید؟
          </Alert>

          <Text fontSize="xs" color={textMuted}>
            توجه: با حذف این نود، تمامی زیرمجموعه‌ها و شاخه‌های فرزند آن نیز طبق مدل Nested Set حذف خواهند شد و شماره‌گذاری درخت مجدداً بازسازی می‌شود.
          </Text>

          <Flex justify="flex-end" gap={2} pt={3}>
            <Button variant="ghost" onClick={onCloseDeleteModal}>
              انصراف
            </Button>
            <Button
              colorScheme="red"
              onClick={() => deleteMutation.mutate(nodeToDelete._id)}
              isLoading={deleteMutation.isLoading}
            >
              حذف قطعی
            </Button>
          </Flex>
        </VStack>
      </CustomModal>

      <CustomModal
        isOpen={isPurgeModalOpen}
        onClose={onClosePurgeModal}
        title="پاکسازی تنوع‌های خارج از کدینگ"
      >
        <VStack spacing={4} align="stretch">
          <Alert status="warning" borderRadius="md" fontSize="xs">
            <AlertIcon />
            همهٔ تنوع‌های منطقی که کد حسابداری‌شان در درخت کدینگ نیست از محصولات حذف می‌شوند.
          </Alert>
          <Text fontSize="xs" color={textMuted}>
            اتصال کدینگ به این تنوع‌ها قطع می‌شود؛ تنوع‌های درگاه باقی می‌مانند ولی از تنوع منطقی جدا می‌شوند. این عمل برگشت‌پذیر نیست.
          </Text>
          <Flex justify="flex-end" gap={2} pt={3}>
            <Button variant="ghost" onClick={onClosePurgeModal}>
              انصراف
            </Button>
            <Button
              colorScheme="orange"
              leftIcon={<FaBroom />}
              onClick={() => purgeMutation.mutate()}
              isLoading={purgeMutation.isLoading}
            >
              پاکسازی
            </Button>
          </Flex>
        </VStack>
      </CustomModal>
    </Box>
  );
}
